import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CopyReadingButton } from "../components/CopyReadingButton";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Sun, Moon, MoonStar, Heart, Zap, Briefcase, Sparkles, Bell, BellOff, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Star, Crown, CloudSun, PenLine } from "lucide-react";
import Coppia from "./Coppia";

const SECTION_ICONS = { amore: Heart, energia: Zap, lavoro: Briefcase };

const CITIES = ["Roma", "Milano", "Napoli", "Torino", "Palermo", "Genova", "Bologna", "Firenze", "Bari", "Catania", "Venezia", "Verona", "Padova", "Trieste", "Brescia", "Parma", "Taranto", "Modena", "Perugia", "Livorno", "Cagliari", "Foggia", "Rimini", "Salerno", "Ferrara", "Pescara", "Bergamo", "Trento", "Bolzano", "Ancona", "Udine", "Lecce", "Pisa", "Como", "Treviso", "Siracusa", "Messina", "Reggio Calabria", "Sassari", "Potenza", "L'Aquila", "Aosta", "Campobasso"];

const NOTIFY_KEY = "arcana_horoscope_notify";

async function scheduleNativeNotification(enabled, time) {
  try {
    const { Capacitor } = await import("@capacitor/core");
    if (!Capacitor.isNativePlatform()) return "web";
    const { LocalNotifications } = await import("@capacitor/local-notifications");
    const perm = await LocalNotifications.requestPermissions();
    if (perm.display !== "granted") return "denied";
    await LocalNotifications.cancel({ notifications: [{ id: 4242 }] });
    if (enabled) {
      const [h, m] = (time || "08:00").split(":").map(Number);
      await LocalNotifications.schedule({
        notifications: [{
          id: 4242,
          title: "Il tuo oroscopo del giorno",
          body: "Le stelle di oggi ti aspettano in Arcana.",
          schedule: { every: "day", on: { hour: h, minute: m }, allowWhileIdle: true },
        }],
      });
      await LocalNotifications.addListener("localNotificationActionPerformed", () => {
        window.location.href = "/cielo";
      });
    }
    return "ok";
  } catch (e) {
    console.warn("Notifica quotidiana non impostata:", e);
    return "error";
  }
}

const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const todayISO = () => toISO(new Date());
const labelDay = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
const shiftDay = (iso, days) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + days); return toISO(d); };

const BirthForm = ({ onSaved }) => {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [place, setPlace] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!date || busy) return;
    setBusy(true);
    try {
      await api.put("/profile/birthdate", {
        birth_date: date,
        birth_time: time || null,
        birth_place: place || null,
      });
      toast.success("Dati di nascita salvati.");
      onSaved();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="cielo-card p-6 sm:p-8 space-y-5" data-testid="cielo-birth-form">
      <p className="eyebrow">Per il tuo oroscopo personalizzato</p>
      <p className="text-sm text-purple-200/70 leading-relaxed">
        Il tuo oroscopo viene calcolato sulle posizioni reali dei pianeti di oggi rispetto alla tua carta natale.
        La data di nascita è necessaria; ora e luogo sono facoltativi ma rendono il calcolo più preciso.
      </p>
      <div>
        <label className="eyebrow block mb-2">Data di nascita *</label>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required data-testid="cielo-birth-date" className="input-dark" />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="eyebrow block mb-2">Ora di nascita (facoltativa)</label>
          <input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="cielo-birth-time" className="input-dark" />
        </div>
        <div>
          <label className="eyebrow block mb-2">Luogo di nascita (facoltativo)</label>
          <input list="cielo-cities" value={place} onChange={(e) => setPlace(e.target.value)} placeholder="Es. Roma" data-testid="cielo-birth-place" className="input-dark" />
          <datalist id="cielo-cities">
            {CITIES.map((c) => <option key={c} value={c} />)}
          </datalist>
        </div>
      </div>
      <p className="text-[11px] text-purple-300/50 leading-relaxed">
        Senza ora e luogo l'ascendente non viene calcolato e la Luna natale è approssimata a mezzogiorno: l'oroscopo resta valido, solo un po' meno puntale.
      </p>
      <button type="submit" disabled={busy || !date} data-testid="cielo-birth-save" className="gold-btn px-8 py-3 text-sm disabled:opacity-50">
        {busy ? "Salvataggio…" : "Calcola il mio oroscopo"}
      </button>
    </form>
  );
};

export default function Cielo() {
  const { user, loading: authLoading } = useAuth();
  const [horo, setHoro] = useState({ loading: true, data: null, premium: false });
  const [date, setDate] = useState(todayISO());
  const [day, setDay] = useState(null);
  const [birthDate, setBirthDate] = useState("");
  const [savingSign, setSavingSign] = useState(false);
  const [editingSign, setEditingSign] = useState(false);
  const [showTransits, setShowTransits] = useState(false);
  const [notify, setNotify] = useState(() => {
    try { return JSON.parse(localStorage.getItem(NOTIFY_KEY)) || { enabled: false, time: "08:00" }; }
    catch { return { enabled: false, time: "08:00" }; }
  });
  const loadHoro = useCallback(() => {
    api.get("/horoscope/today")
      .then((r) => setHoro({ loading: false, data: r.data, premium: false }))
      .catch((e) => {
        if (e?.response?.status === 403 && e?.response?.data?.detail?.code === "premium_required") {
          setHoro({ loading: false, data: null, premium: true });
        } else {
          setHoro({ loading: false, data: null, premium: false });
        }
      });
  }, []);

  const loadDay = useCallback((d) => {
    api.get(`/diary/${d}`).then((r) => setDay(r.data)).catch(() => setDay(null));
  }, []);

  useEffect(() => { if (user) loadHoro(); }, [user, loadHoro]);
  useEffect(() => { if (user) loadDay(date); }, [user, date, loadDay]);

  const saveNotify = async (next) => {
    setNotify(next);
    localStorage.setItem(NOTIFY_KEY, JSON.stringify(next));
    const res = await scheduleNativeNotification(next.enabled, next.time);
    if (res === "ok") toast.success(next.enabled ? "Notifica quotidiana attivata." : "Notifica quotidiana disattivata.");
    else if (res === "web") toast.info("Le notifiche automatiche sono disponibili nell'app Android di Arcana. Preferenza salvata.");
    else if (res === "denied") toast.error("Il dispositivo ha negato il permesso alle notifiche.");
    else toast.error("Impossibile impostare la notifica.");
  };

  const saveSign = async () => {
    if (!birthDate) return;
    setSavingSign(true);
    try {
      const r = await api.put("/profile/birthdate", { birth_date: birthDate });
      toast.success(`Il tuo segno è ${r.data.zodiac_sign}: il tuo cielo è pronto.`);
      setEditingSign(false);
      loadDay(date);
      loadHoro();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSavingSign(false);
    }
  };

  if (authLoading) return null;

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-4 py-24 text-center" data-testid="cielo-login-required">
        <div className="cielo-card p-10">
          <MoonStar className="w-10 h-10 text-amber-400/60 mx-auto mb-5" />
          <h1 className="font-display text-3xl text-amber-100 mb-4">Il mio cielo</h1>
          <p className="text-purple-200/70 text-sm mb-8">
            Oroscopo, Luna e illuminazione del giorno in un unico spazio personale: accedi o crea un account per leggerli.
          </p>
          <Link to="/accedi?redirect=/cielo" data-testid="cielo-login-btn" className="gold-btn inline-block px-8 py-3 text-sm">
            Accedi o registrati
          </Link>
        </div>
      </div>
    );
  }

  const isToday = date === todayISO();
  const showSignForm = day && (!day.has_sign || editingSign);

  return (
    <div className="max-w-2xl mx-auto px-4 lg:px-8 pt-24 sm:pt-28 pb-16" data-testid="cielo-page">
      <div className="text-center mb-10">
        <p className="eyebrow">Il tuo quotidiano</p>
        <h1 className="font-display text-4xl sm:text-5xl text-amber-100 mt-3">Il mio cielo</h1>
        <p className="text-purple-200/60 text-sm mt-4 max-w-md mx-auto leading-relaxed">
          Oroscopo, Luna e illuminazione del giorno, raccolti in un unico spazio.
        </p>
      </div>

      <div className="space-y-6">
        <section data-testid="cielo-oroscopo-section">
          <div className="flex items-center gap-3 mb-4">
            <Sun className="w-5 h-5 text-amber-400" />
            <h2 className="font-display text-xl text-amber-100">Oroscopo di oggi</h2>
          </div>
          {horo.loading ? (
            <p className="text-center text-purple-300/50 text-sm py-6" data-testid="cielo-oroscopo-loading">Leggo il cielo di oggi…</p>
          ) : horo.premium ? (
            <div className="cielo-card p-8 text-center" data-testid="cielo-oroscopo-gate">
              <Crown className="w-7 h-7 text-amber-400 mx-auto mb-4" />
              <p className="text-purple-100/85 text-sm leading-relaxed max-w-md mx-auto mb-6">
                L'oroscopo di oggi — calcolato sulle posizioni reali dei pianeti rispetto alla tua carta natale —
                è riservato agli abbonati di Arcana. Non consuma consultazioni: con l'abbonamento è sempre disponibile.
              </p>
              <Link to="/piani" data-testid="cielo-oroscopo-gate-cta" className="gold-btn px-8 py-3 text-sm inline-block">
                Scopri l'abbonamento
              </Link>
            </div>
          ) : !horo.data ? null : horo.data.needs_birth_data ? (
            <BirthForm onSaved={() => { loadHoro(); loadDay(date); }} />
          ) : (
            <div className="cielo-card p-6 sm:p-8 space-y-5" data-testid="cielo-oroscopo-result">
              <div className="flex flex-wrap items-center justify-center gap-2 text-center">
                <span className="text-[11px] uppercase tracking-wider border border-amber-500/40 text-amber-200 rounded-full px-3 py-1 inline-flex items-center gap-1.5" data-testid="cielo-oroscopo-moon-sign">
                  <Moon className="w-3 h-3" /> Luna in {horo.data.moon_sign}
                </span>
                <span className="text-[11px] uppercase tracking-wider border border-[#4a2175] text-purple-200 rounded-full px-3 py-1" data-testid="cielo-oroscopo-moon-phase">
                  {horo.data.moon_phase}
                </span>
                {horo.data.zodiac_sign && (
                  <span className="text-[11px] uppercase tracking-wider border border-[#4a2175] text-purple-200 rounded-full px-3 py-1 inline-flex items-center gap-1.5" data-testid="cielo-oroscopo-sun-sign">
                    <Sun className="w-3 h-3" /> {horo.data.zodiac_sign}
                  </span>
                )}
                {horo.data.ascendant && (
                  <span className="text-[11px] uppercase tracking-wider border border-[#4a2175] text-purple-200 rounded-full px-3 py-1" data-testid="cielo-oroscopo-ascendant">
                    Ascendente {horo.data.ascendant}
                  </span>
                )}
              </div>

              <p className="text-purple-100/85 text-sm leading-relaxed text-center italic" data-testid="cielo-oroscopo-opening">
                {horo.data.opening}
              </p>
              <div className="flex justify-center gap-2 flex-wrap">
                <CopyReadingButton testid="copy-cielo-oroscopo" text={[horo.data.opening, ...horo.data.sections.map((s) => `${s.label}\n${s.text}`)].join("\n\n")} />
              </div>

              <div className="space-y-4 pt-2">
                {horo.data.sections.map((s) => {
                  const Icon = SECTION_ICONS[s.key] || Sparkles;
                  return (
                    <div key={s.key} className="border-t border-[#4a2175]/50 pt-4" data-testid={`cielo-oroscopo-section-${s.key}`}>
                      <p className="text-[11px] uppercase tracking-[0.2em] text-amber-300/80 mb-2 flex items-center gap-2">
                        <Icon className="w-3.5 h-3.5" /> {s.label}
                      </p>
                      <p className="text-sm text-purple-100/80 leading-relaxed">{s.text}</p>
                    </div>
                  );
                })}
              </div>

              {horo.data.peak && (
                <div className="border border-amber-500/30 rounded-xl p-4 bg-amber-500/5" data-testid="cielo-oroscopo-peak">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-amber-300/80 mb-2 flex items-center gap-2">
                    <Sparkles className="w-3.5 h-3.5" /> Momento significativo
                  </p>
                  <p className="text-sm text-amber-100/90 leading-relaxed">{horo.data.peak}</p>
                </div>
              )}

              {horo.data.retrogrades.length > 0 && (
                <div className="space-y-2" data-testid="cielo-oroscopo-retrogrades">
                  {horo.data.retrogrades.map((r, i) => (
                    <p key={i} className="text-xs text-purple-300/60 leading-relaxed italic">↩ {r}</p>
                  ))}
                </div>
              )}

              {horo.data.approximate_moon && (
                <p className="text-[11px] text-purple-300/40 leading-relaxed" data-testid="cielo-oroscopo-approx-note">
                  Senza ora di nascita la tua Luna natale è approssimata a mezzogiorno. Aggiungila per un oroscopo più preciso.
                </p>
              )}

              <button onClick={() => setShowTransits(!showTransits)} data-testid="cielo-oroscopo-transits-toggle"
                className="text-[11px] text-purple-300/60 hover:text-purple-200 inline-flex items-center gap-1.5 transition-colors">
                {showTransits ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                {showTransits ? "Nascondi i passaggi di oggi" : "I passaggi di oggi sul tuo cielo"}
              </button>
              {showTransits && (
                <ul className="space-y-1.5 text-xs text-purple-300/60" data-testid="cielo-oroscopo-transits-list">
                  {(horo.data.transits_debug || []).map((t, i) => <li key={i}>· {t}</li>)}
                </ul>
              )}
            </div>
          )}
        </section>

        <div className="flex items-center justify-center gap-4 pt-2" data-testid="cielo-date-nav">
          <button
            onClick={() => setDate(shiftDay(date, -1))}
            data-testid="cielo-prev-day"
            aria-label="Giorno precedente"
            className="w-10 h-10 rounded-full border border-[#4a2175] text-purple-300/70 hover:border-amber-500/60 hover:text-amber-300 transition-colors flex items-center justify-center"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="text-center">
            <p className="font-display text-xl text-amber-100 capitalize" data-testid="cielo-date">{labelDay(date)}</p>
            {!isToday && (
              <button onClick={() => setDate(todayISO())} data-testid="cielo-today-btn" className="text-xs text-amber-300/80 hover:text-amber-200 transition-colors mt-1">
                Torna a oggi
              </button>
            )}
          </div>
          <button
            onClick={() => setDate(shiftDay(date, 1))}
            disabled={isToday}
            data-testid="cielo-next-day"
            aria-label="Giorno successivo"
            className="w-10 h-10 rounded-full border border-[#4a2175] text-purple-300/70 hover:border-amber-500/60 hover:text-amber-300 transition-colors flex items-center justify-center disabled:opacity-30 disabled:pointer-events-none"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {day && (
          <>
            <div className="cielo-card p-7" data-testid="cielo-moon-card">
              <div className="flex items-center gap-3 mb-3">
                <MoonStar className="w-5 h-5 text-amber-400" />
                <h2 className="font-display text-lg text-amber-100">La Luna di {isToday ? "oggi" : "quel giorno"}</h2>
              </div>
              <p className="text-purple-100/85 text-sm">
                <span className="text-amber-200 font-medium" data-testid="cielo-moon-phase">{day.moon.phase}</span>
                <span className="text-purple-300/60"> · illuminazione {day.moon.illumination}% · in fase {day.moon.waxing ? "crescente" : "calante"}</span>
              </p>
              <div className="mt-4 flex justify-center">
                <CopyReadingButton testid="copy-cielo-moon" text={`La Luna di oggi: ${day.moon.phase} · illuminazione ${day.moon.illumination}% · in fase ${day.moon.waxing ? "crescente" : "calante"}`} />
              </div>
            </div>

            <div className="cielo-card p-7" data-testid="cielo-illuminazione-card">
              <div className="flex items-center gap-3 mb-3">
                <Sparkles className="w-5 h-5 text-amber-400" />
                <h2 className="font-display text-lg text-amber-100">L'illuminazione di oggi</h2>
              </div>
              <p className="text-purple-100/85 text-sm italic leading-relaxed" data-testid="cielo-illuminazione-text">“{day.illuminazione}”</p>
              <div className="mt-4 flex justify-center">
                <CopyReadingButton testid="copy-cielo-illuminazione" text={day.illuminazione} />
              </div>
            </div>

            <div className="cielo-card p-7" data-testid="cielo-sign-card">
              <div className="flex items-center gap-3 mb-3">
                <CloudSun className="w-5 h-5 text-amber-400" />
                <h2 className="font-display text-lg text-amber-100">Il mio cielo</h2>
              </div>
              {showSignForm ? (
                <div data-testid="cielo-sign-form">
                  <p className="text-purple-200/70 text-sm mb-5">
                    {day.has_sign
                      ? "Modifica la tua data di nascita: il tuo cielo si aggiornerà subito."
                      : "Per leggere il tuo cielo quotidiano mi serve solo la tua data di nascita: la chiederò una volta sola."}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input
                      type="date"
                      value={birthDate}
                      onChange={(e) => setBirthDate(e.target.value)}
                      max={todayISO()}
                      data-testid="cielo-sign-date-input"
                      className="input-dark flex-1"
                    />
                    <button
                      onClick={saveSign}
                      disabled={savingSign || !birthDate}
                      data-testid="cielo-sign-save-btn"
                      className="gold-btn px-6 py-2.5 text-xs disabled:opacity-50"
                    >
                      {savingSign ? "Un attimo…" : day.has_sign ? "Salva la data" : "Scopri il tuo cielo"}
                    </button>
                    {day.has_sign && (
                      <button
                        onClick={() => setEditingSign(false)}
                        data-testid="cielo-sign-cancel"
                        className="text-xs text-purple-300/70 hover:text-amber-200 transition-colors"
                      >
                        Annulla
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-purple-100/85 text-sm leading-relaxed" data-testid="cielo-sign-text">{day.cielo}</p>
                  <div className="mt-4 flex justify-center">
                    <CopyReadingButton testid="copy-cielo-sign" text={day.cielo} />
                  </div>
                  <button
                    onClick={() => setEditingSign(true)}
                    data-testid="cielo-sign-edit"
                    className="mt-4 text-xs text-purple-300/60 hover:text-amber-200 transition-colors inline-flex items-center gap-1.5"
                  >
                    <PenLine className="w-3.5 h-3.5" /> Modifica la tua data di nascita
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        <div className="cielo-card p-6" data-testid="cielo-notify-card">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              {notify.enabled ? <Bell className="w-5 h-5 text-amber-300" /> : <BellOff className="w-5 h-5 text-purple-300/50" />}
              <div>
                <p className="text-sm text-amber-100">Notifica quotidiana</p>
                <p className="text-[11px] text-purple-300/50">Un invito a leggere il tuo cielo, ogni giorno. Facoltativa e disattivabile.</p>
              </div>
            </div>
            <button
              onClick={() => saveNotify({ ...notify, enabled: !notify.enabled })}
              data-testid="cielo-notify-toggle"
              className={`text-xs rounded-full px-4 py-2 border transition-colors ${notify.enabled ? "border-amber-500/50 text-amber-200 bg-amber-500/10" : "border-[#4a2175] text-purple-300/70 hover:border-amber-500/40"}`}
            >
              {notify.enabled ? "Attiva" : "Disattivata"}
            </button>
          </div>
          {notify.enabled && (
            <div className="flex items-center gap-3 mt-4 pt-4 border-t border-[#4a2175]/50">
              <label className="text-xs text-purple-300/60">Orario:</label>
              <input type="time" value={notify.time} onChange={(e) => saveNotify({ ...notify, time: e.target.value })}
                data-testid="cielo-notify-time" className="input-dark w-auto" />
            </div>
          )}
        </div>

        <section data-testid="cielo-coppia-section" className="pt-8 border-t border-[#4a2175]/50">
          <div className="flex items-center gap-3 mb-5">
            <Heart className="w-5 h-5 text-amber-400" />
            <h2 className="font-display text-xl text-amber-100">La nostra coppia</h2>
          </div>
          <Coppia embedded />
        </section>
      </div>
    </div>
  );
}
